/**
 * WhatsAppTextModal — envia tabela como texto formatado via wa.me.
 *
 * A formatação do texto (por tool, dispatch por `email_template`) vive em
 * `utils/whatsapp-templates.ts` — este arquivo é só o componente/UI (mesma
 * separação que `EmailReportModal.tsx` + `utils/emailTemplates.ts`).
 *
 * Para tabelas visuais (Pedidos CSPS, com cor por linha), usar
 * WhatsAppMessageModal (PNG via canvas) em vez deste.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Icon } from '@/design-system/atoms/Icon'
import { Spinner } from '@/design-system/atoms/Spinner'
import type { TableData } from '../../types/tableData'
import type { UserListItem } from '../../services/emailService'
import { getUsers } from '../../services/emailService'
import { buildWhatsAppMessage } from '../../utils/whatsapp-templates'

interface WhatsAppTextModalProps {
  data: TableData
  isOpen: boolean
  onClose: () => void
}

export const WhatsAppTextModal: React.FC<WhatsAppTextModalProps> = ({
  data,
  isOpen,
  onClose,
}) => {
  const [users, setUsers] = useState<UserListItem[]>([])
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [editedMessage, setEditedMessage] = useState('')
  const [copied, setCopied] = useState(false)

  const generatedMessage = useMemo(() => buildWhatsAppMessage(data), [data])

  useEffect(() => {
    if (!isOpen) return
    setEditedMessage(generatedMessage)
    setCopied(false)

    setLoading(true)
    getUsers()
      .then(setUsers)
      .catch(() => setUsers([]))
      .finally(() => setLoading(false))
  }, [isOpen, generatedMessage])

  const usersWithPhone = useMemo(
    () => users.filter(u => u.phone && u.phone.length >= 10),
    [users]
  )

  const filteredUsers = useMemo(() => {
    if (!search.trim()) return usersWithPhone
    const term = search.toLowerCase()
    return usersWithPhone.filter(
      u => u.full_name.toLowerCase().includes(term) || u.phone.includes(term)
    )
  }, [usersWithPhone, search])

  const selectedUser = useMemo(
    () => users.find(u => u.id === selectedId) ?? null,
    [users, selectedId]
  )

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(editedMessage)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      const textarea = document.createElement('textarea')
      textarea.value = editedMessage
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }, [editedMessage])

  const handleSend = useCallback(() => {
    if (!selectedUser?.phone) return

    const phone = selectedUser.phone
    const encoded = encodeURIComponent(editedMessage)
    window.open(`https://wa.me/${phone}?text=${encoded}`, '_blank')
    onClose()
  }, [selectedUser, editedMessage, onClose])

  if (!isOpen) return null

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />

      <div className="fixed inset-0 z-50 flex items-end lg:items-center justify-center p-0 lg:p-4">
        <div
          className="bg-white dark:bg-gray-800 rounded-t-2xl lg:rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] h-[80vh] flex flex-col"
          onClick={e => e.stopPropagation()}
        >
          <div className="border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-green-500"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                  Enviar por WhatsApp
                </h3>
              </div>
              <button
                onClick={onClose}
                className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
              >
                <Icon name="x" size={16} className="text-gray-500" />
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {data.title} — selecione o destinatario e edite a mensagem se
              necessario
            </p>
          </div>

          <div className="flex flex-col lg:flex-row flex-1 min-h-0 overflow-hidden">
            <div className="lg:w-1/3 flex-shrink-0 flex flex-col border-b lg:border-b-0 lg:border-r border-gray-100 dark:border-gray-700 max-h-[30vh] lg:max-h-none">
              <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700 flex-shrink-0">
                <input
                  type="text"
                  placeholder="Buscar..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full px-2.5 py-1.5 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-gray-900 dark:text-white placeholder-gray-400"
                />
              </div>

              <div className="flex-1 overflow-y-auto px-2 py-1">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Spinner size="md" className="text-green-500" />
                  </div>
                ) : usersWithPhone.length === 0 ? (
                  <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-8">
                    Nenhum usuario com telefone cadastrado
                  </p>
                ) : filteredUsers.length === 0 ? (
                  <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-8">
                    Nenhum resultado
                  </p>
                ) : (
                  filteredUsers.map(user => {
                    const isSelected = selectedId === user.id
                    return (
                      <label
                        key={user.id}
                        className={`flex items-center gap-2 px-2 py-2 rounded-lg cursor-pointer transition-colors ${
                          isSelected
                            ? 'bg-green-50 dark:bg-green-900/20'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        <input
                          type="radio"
                          name="whatsapp-recipient"
                          checked={isSelected}
                          onChange={() => setSelectedId(user.id)}
                          className="w-3.5 h-3.5 text-green-500 focus:ring-green-500 border-gray-300"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {user.full_name}
                          </div>
                          <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                            +{user.phone.slice(0, 2)} ({user.phone.slice(2, 4)}){' '}
                            {user.phone.slice(4)}
                          </div>
                        </div>
                      </label>
                    )
                  })
                )}
              </div>
            </div>

            <div className="flex-1 flex flex-col px-4 py-2 min-h-0">
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[10px] uppercase font-semibold text-gray-400 dark:text-gray-500 tracking-wide">
                  Mensagem
                </div>
                <button
                  onClick={handleCopy}
                  className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  {copied ? (
                    <>
                      <Icon name="check" size={12} className="text-green-500" />
                      Copiado!
                    </>
                  ) : (
                    <>
                      <Icon name="copy" size={12} />
                      Copiar
                    </>
                  )}
                </button>
              </div>
              <textarea
                value={editedMessage}
                onChange={e => setEditedMessage(e.target.value)}
                className="flex-1 w-full px-3 py-2.5 text-sm bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800/30 rounded-lg text-gray-800 dark:text-gray-200 leading-relaxed resize-none focus:outline-none focus:ring-2 focus:ring-green-500 font-sans"
              />
            </div>
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-3 flex-shrink-0">
            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSend}
                disabled={!selectedUser?.phone}
                className="flex-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors flex items-center justify-center gap-1.5"
              >
                <svg
                  className="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                Abrir WhatsApp
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
